import React from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { ChevronRight, Home } from 'lucide-react';

const BreadcrumbContainer = styled.nav`
  margin-bottom: ${({ theme }) => theme.spacing[6]};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    margin-bottom: ${({ theme }) => theme.spacing[4]};
  }
`;

const BreadcrumbList = styled.ol`
  display: flex;
  align-items: center;
  list-style: none;
  margin: 0;
  padding: 0;
  gap: ${({ theme }) => theme.spacing[2]};
  flex-wrap: wrap;
`;

const BreadcrumbItem = styled.li`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  margin: 0;

  &:not(:last-child)::after {
    content: '';
    display: flex;
    align-items: center;
  }
`;

const BreadcrumbLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[1]};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-decoration: none;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  transition: color ${({ theme }) => theme.transitions.fast};
  padding: ${({ theme }) => theme.spacing[1]} ${({ theme }) => theme.spacing[2]};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:hover {
    color: ${({ theme }) => theme.colors.primary[600]};
    text-decoration: none;
    background-color: ${({ theme }) => theme.colors.primary[50]};
  }

  svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }
`;

const BreadcrumbText = styled.span`
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  padding: ${({ theme }) => theme.spacing[1]} ${({ theme }) => theme.spacing[2]};
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Separator = styled(ChevronRight)`
  width: 16px;
  height: 16px;
  color: ${({ theme }) => theme.colors.text.tertiary};
  flex-shrink: 0;
`;

const breadcrumbMap: Record<string, string> = {
  '/': 'Dashboard',
  '/results': 'Results',
  '/processing': 'Processing Status',
};

const getBreadcrumbLabel = (path: string, params: Record<string, string | undefined>): string => {
  // Handle dynamic routes
  if (path.includes('/results/') && params.registrationNumber) {
    return `Result: ${params.registrationNumber}`;
  }
  
  // Handle static routes
  return breadcrumbMap[path] || path.split('/').pop() || path;
};

export const Breadcrumbs: React.FC = () => {
  const location = useLocation();
  const params = useParams();
  const pathnames = location.pathname.split('/').filter(x => x);

  // Don't show breadcrumbs on home page
  if (location.pathname === '/') {
    return null;
  }

  const breadcrumbs = [
    { path: '/', label: 'Dashboard' },
    ...pathnames.map((pathname, index) => {
      const path = `/${pathnames.slice(0, index + 1).join('/')}`;
      const label = getBreadcrumbLabel(path, params);
      return { path, label };
    }),
  ];

  // Remove duplicate dashboard entries
  const uniqueBreadcrumbs = breadcrumbs.filter((breadcrumb, index, array) => {
    if (index === 0) return true; // Always keep the first dashboard
    return breadcrumb.path !== array[0].path;
  });

  return (
    <BreadcrumbContainer aria-label="Breadcrumb navigation">
      <BreadcrumbList>
        {uniqueBreadcrumbs.map((breadcrumb, index) => (
          <React.Fragment key={breadcrumb.path}>
            <BreadcrumbItem>
              {index === uniqueBreadcrumbs.length - 1 ? (
                <BreadcrumbText title={breadcrumb.label}>
                  {breadcrumb.label}
                </BreadcrumbText>
              ) : (
                <BreadcrumbLink to={breadcrumb.path} title={breadcrumb.label}>
                  {index === 0 && <Home />}
                  {breadcrumb.label}
                </BreadcrumbLink>
              )}
            </BreadcrumbItem>
            {index < uniqueBreadcrumbs.length - 1 && <Separator />}
          </React.Fragment>
        ))}
      </BreadcrumbList>
    </BreadcrumbContainer>
  );
};